import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SocialMediaUsernameSetup from './SocialMediaUsernameSetup';
import SocialMediaDataSync from './SocialMediaDataSync';

interface SocialMediaSetupCheckProps {
  children: React.ReactNode;
}

type SetupStep = 'checking' | 'username-setup' | 'data-sync' | 'complete';

const SocialMediaSetupCheck: React.FC<SocialMediaSetupCheckProps> = ({ children }) => {
  const { business, loading: authLoading } = useAuth();
  const [step, setStep] = useState<SetupStep>('checking');
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!business) {
      return;
    }

    // sosyal medya varlığını kontrol et (username veya bağlantı/senkron durumu)
    const hasAnySocialPresence = (() => {
      const fb = business.facebook;
      const ig = business.instagram;
      const tw = business.twitter;
      const tt = business.tiktok;
      const li = business.linkedin;
      const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
        !!(p?.username && p.username.trim()) || p?.isConnected === true;
      return platformHasData(fb) || platformHasData(ig) || platformHasData(tw) || platformHasData(tt) || platformHasData(li);
    })();

    // Veri çekme işleminin devam edip etmediğini kontrol et
    const isSyncing = localStorage.getItem('socialMediaDataSyncing') === 'true';

    if (!hasAnySocialPresence) {
      // Username yok, setup ekranını göster
      setStep('username-setup');
    } else if (isSyncing) {
      // Username var ama veri çekme devam ediyor
      setStep('data-sync');
    } else {
      // Her şey tamam
      setStep('complete');
    }
  }, [business, authLoading]);

  const handleUsernameSetupComplete = () => {
    // Username'ler kaydedildi, doğrudan dashboard'a yönlendir
    localStorage.removeItem('socialMediaDataSyncing');
    navigate('/social-media');
  };

  // Eğer sunucu senkronu tamamlandı işaretlediyse, local flag'i temizle
  useEffect(() => {
    const anyCompleted =
      business?.facebook?.syncStatus === 'completed' ||
      business?.instagram?.syncStatus === 'completed' ||
      business?.twitter?.syncStatus === 'completed' ||
      business?.tiktok?.syncStatus === 'completed' ||
      business?.linkedin?.syncStatus === 'completed';
    if (anyCompleted) {
      localStorage.removeItem('socialMediaDataSyncing');
    }
  }, [business?.facebook?.syncStatus, business?.instagram?.syncStatus, business?.twitter?.syncStatus, business?.tiktok?.syncStatus, business?.linkedin?.syncStatus]);

  const handleDataSyncComplete = () => {
    // Veri çekme tamamlandı
    localStorage.removeItem('socialMediaDataSyncing');
    setStep('complete');
  };

  if (authLoading || step === 'checking') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (step === 'username-setup') {
    return <SocialMediaUsernameSetup onComplete={handleUsernameSetupComplete} />;
  }

  if (step === 'data-sync') {
    return <SocialMediaDataSync onComplete={handleDataSyncComplete} />;
  }

  // step === 'complete'
  return <>{children}</>;
};

export default SocialMediaSetupCheck;

